import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UtensilsCrossed, Flame, Beef } from "lucide-react";
import api from "../lib/api";
import { Loader } from "../components/common";

const MEAL_COLORS = { Breakfast: "text-warning", Lunch: "text-brand", Snack: "text-success", Dinner: "text-chart-2" };

export default function Nutrition() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);

  useEffect(() => { api.get("/customer/meal-plans").then(({ data }) => setPlans(data)).catch(() => setPlans([])); }, []);
  if (!plans) return <Loader />;

  return (
    <div className="p-5">
      <button data-testid="nutrition-back" onClick={() => navigate("/app")} className="flex items-center gap-2 text-secondary text-sm hover:text-white"><ArrowLeft className="w-4 h-4" /> Back</button>
      <h1 className="font-heading text-4xl mt-3">Nutrition</h1>
      <p className="text-secondary text-sm mt-1">Meal plans from your coach.</p>

      {plans.length === 0 && (
        <div className="card-surface rounded-xl p-8 text-center mt-6">
          <UtensilsCrossed className="w-10 h-10 text-tertiary mx-auto" />
          <p className="text-secondary text-sm mt-3">No meal plan yet. Your coach will share one soon.</p>
        </div>
      )}

      <div className="space-y-6 mt-6">
        {plans.map((p) => (
          <div key={p.id} className="card-surface rounded-2xl p-5" data-testid="meal-plan-card">
            <h2 className="font-heading text-3xl">{p.title}</h2>
            {p.description && <p className="text-secondary text-sm mt-1">{p.description}</p>}

            <div className="grid grid-cols-4 gap-2 mt-4">
              {[["Cal", p.daily_calories, "kcal"], ["Protein", p.protein_g, "g"], ["Carbs", p.carbs_g, "g"], ["Fat", p.fat_g, "g"]].map(([l, v, u]) => (
                <div key={l} className="bg-white/5 rounded-lg p-2.5 text-center">
                  <p className="label-xs">{l}</p>
                  <p className="font-heading text-2xl leading-none mt-1">{v ?? "—"}</p>
                  <p className="text-[10px] text-tertiary">{u}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 space-y-3">
              {p.meals?.map((m, i) => (
                <div key={i} className="border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UtensilsCrossed className={`w-4 h-4 ${MEAL_COLORS[m.type] || "text-brand"}`} />
                      <span className="label-xs">{m.type}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-secondary">
                      {m.calories != null && <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {m.calories}</span>}
                      {m.protein_g != null && <span className="flex items-center gap-1"><Beef className="w-3 h-3" /> {m.protein_g}g</span>}
                    </div>
                  </div>
                  <p className="font-semibold mt-1.5">{m.name}</p>
                  {m.description && <p className="text-sm text-secondary mt-0.5">{m.description}</p>}
                  {m.items?.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {m.items.map((it, j) => <li key={j} className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-secondary">{it}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {p.notes && <div className="mt-4 bg-brand/10 border border-brand/20 rounded-lg p-3"><p className="label-xs mb-1">Coach Notes</p><p className="text-sm text-secondary">{p.notes}</p></div>}
          </div>
        ))}
      </div>
    </div>
  );
}
